/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconBulkEdit from '@apollo/icons/default/IconBulkEdit.svg';
import { Button, IconButton } from '@apollo/orbit';
import { jsx } from '@emotion/react';
import produce from 'immer';
import { isEqual } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { HeadersEditor } from 'src/components/headersEditor/HeadersEditor';
import { NewHeaderButton } from 'src/components/headersEditor/NewHeaderButton';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import Config from 'src/lib/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GraphRef } from '../../hooks/useGraphRef';
import { GraphBehavioralFlags } from '../../variantLayout/graphBehavioralFlags/graphBehavioralFlags';
import { ExplorerBehavioralFlags } from '../explorerBehavioralFlags';
import { HeaderEntry } from '../hooks/useExplorerState/useHeadersManagerContext/shared';
import { useHeadersManagerContext } from '../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';

type EditHeadersMode = 'KeyValue' | 'Bulk';

const headerEntriesToBulkHeaders = (headerEntries: HeaderEntry[]): string => {
  const bulkHeaders = headerEntries
    .map(
      (entry) =>
        `${!entry.checked ? '// ' : ''}${entry.headerName}:${entry.value}`,
    )
    .join('\n');

  return bulkHeaders === ':' ? '' : bulkHeaders;
};

const updateHeaderEntriesWithBulkHeaders = (
  bulkHeaders: string,
  keys: number[],
  setTabbedHeaderDefinitions: React.Dispatch<
    React.SetStateAction<HeaderEntry[]>
  >,
) => {
  const bulkHeaderRows = bulkHeaders.split('\n');

  const newHeaders = bulkHeaderRows.map((row, i) =>
    bulkHeaderRowToHeaderEntry(row, keys[i] ?? Math.random()),
  );

  setTabbedHeaderDefinitions((currentHeaderDefinitions) =>
    produce(currentHeaderDefinitions, (draftHeaders) => {
      newHeaders.forEach((header, index) => {
        if (index >= draftHeaders.length) {
          draftHeaders.push(header);
        } else if (
          draftHeaders[index] &&
          !isEqual(draftHeaders[index], header)
        ) {
          // eslint-disable-next-line no-param-reassign
          draftHeaders[index] = header;
        }
      });
      draftHeaders.splice(newHeaders.length);
    }),
  );
};

const bulkHeaderRowToHeaderEntry = (
  bulkHeaderRow: string,
  key: number,
): HeaderEntry => {
  const row = bulkHeaderRow.trim();
  const checked = !row.startsWith('//');
  const header = checked ? row.trim() : row.slice(2).trim();
  const [headerName, ...rest] = header.split(':');
  const value = rest.join(':');
  return {
    headerName,
    value,
    checked,
    key,
  };
};

export const ExplorerHeadersEditor = ({
  graphRef,
  explorerBehavioralFlags,
  graphBehavioralFlags,
  settingsEndpointUrl,
  settingsSubscriptionUrl,
}: {
  graphRef: GraphRef | null;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  graphBehavioralFlags: GraphBehavioralFlags;
  settingsEndpointUrl: string | null;
  settingsSubscriptionUrl: string | null;
}) => {
  const location = useLocation();
  const placeholderTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const [editHeadersMode, setEditHeadersMode] =
    useState<EditHeadersMode>('KeyValue');

  const {
    sharedHeaderDefinitions,
    setSharedHeaderDefinitions,
    tabbedHeaderDefinitions,
    setTabbedHeaderDefinitions,
  } = useHeadersManagerContext();

  const keys = tabbedHeaderDefinitions.map((header) => header.key);

  const headersEditor = useStudioEditor({
    language: 'text',
    defaultValue: headerEntriesToBulkHeaders(tabbedHeaderDefinitions),
    lineNumbers: 'off',
    glyphMargin: false,
    onChange: (newValue) => {
      if (newValue === '') {
        setCurrentHeaders(newValue);
        placeholderTextAreaRef.current?.focus();
      }
      updateHeaderEntriesWithBulkHeaders(
        newValue,
        keys,
        setTabbedHeaderDefinitions,
      );
    },
  });

  const [currentHeaders, setCurrentHeaders] = useState(
    headersEditor.getValue(),
  );
  useEffect(() => {
    if (currentHeaders !== '') {
      headersEditor.focus();
    }
  }, [currentHeaders, headersEditor]);

  // TODO (jason) The way we store headers right now always includes an empty
  // set. We should not be doing this, we should instead be displaying the empty
  // row if we want to have an immediately editable area, but not by adding
  // something to the stored data.
  const nonEmptySharedHeaders = React.useMemo(
    () =>
      sharedHeaderDefinitions.filter(
        (sharedHeader) => sharedHeader.headerName || sharedHeader.value,
      ),
    [sharedHeaderDefinitions],
  );

  return (
    <>
      {editHeadersMode === 'KeyValue' ? (
        <>
          <HeadersEditor
            className="mt-2"
            headerDefinitions={tabbedHeaderDefinitions}
            setHeaderDefinitions={setTabbedHeaderDefinitions}
            sharedHeaders={nonEmptySharedHeaders}
            settingsEndpointUrl={settingsEndpointUrl}
            settingsSubscriptionUrl={settingsSubscriptionUrl}
            withCheckbox
            setSharedHeaders={setSharedHeaderDefinitions}
            explorerBehavioralFlags={explorerBehavioralFlags}
            headersDisabled={false}
            sharedHeadersDisabled={true}
            sharedHeadersDisabledTooltipContent={
              !graphBehavioralFlags || graphBehavioralFlags.canEditSharedHeaders
                ? 'Click to edit shared headers in connection settings'
                : "You don't have permission to edit the shared headers."
            }
          />

          <div className="mt-auto flex gap-2">
            <Tooltip label="Bulk Edit">
              <IconButton
                aria-label="Bulk edit headers"
                variant="hidden"
                icon={<IconBulkEdit />}
                onClick={() => {
                  const bulkHeaders = headerEntriesToBulkHeaders(
                    tabbedHeaderDefinitions,
                  );
                  headersEditor.setValue(bulkHeaders);
                  setCurrentHeaders(bulkHeaders);
                  setEditHeadersMode('Bulk');
                }}
              />
            </Tooltip>
            <NewHeaderButton
              isSharedHeader={false}
              onClick={() =>
                setTabbedHeaderDefinitions(
                  tabbedHeaderDefinitions.concat({
                    headerName: '',
                    value: '',
                    checked: true,
                    key: Math.random(),
                  }),
                )
              }
            />
            <PermissionGuard
              graphId={graphRef?.graphId}
              graphVariantPermissions="canManageExplorerSettings"
              graphVariant={graphRef?.graphVariant}
              fallback={null}
            >
              <Button
                variant="hidden"
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.connectionSettings,
                })}
              >
                Set shared headers
              </Button>
            </PermissionGuard>
          </div>
        </>
      ) : (
        <>
          {currentHeaders === '' ? (
            <textarea
              ref={placeholderTextAreaRef}
              className="mt-2 h-full resize-none bg-transparent text-sm text-secondary"
              style={{ marginLeft: 26 }}
              onChange={(newValue) => {
                setCurrentHeaders(newValue.target.value);
                headersEditor.setValue(newValue.target.value);
                const lines = newValue.target.value.split('\n');
                headersEditor.setPosition(
                  new monacoEditor.Position(
                    lines.length,
                    (lines[lines.length - 1]?.length ?? 1) + 1,
                  ),
                );
              }}
              placeholder="Headers are separated by new line
Separate keys and values by :
Prepend // to any header you want to add but keep unchecked"
            />
          ) : (
            <StudioEditorContainer editor={headersEditor} className="h-full" />
          )}
          <div className="mt-auto flex gap-2">
            <Tooltip label="Key-Value Edit">
              <Button
                aria-label="Key-value edit"
                variant="hidden"
                leftIcon={<IconArrowLeft />}
                onClick={() => setEditHeadersMode('KeyValue')}
              >
                Back
              </Button>
            </Tooltip>

            <PermissionGuard
              graphId={graphRef?.graphId}
              graphVariantPermissions="canManageExplorerSettings"
              graphVariant={graphRef?.graphVariant}
              fallback={null}
            >
              <Button
                variant="hidden"
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.connectionSettings,
                })}
              >
                Set shared headers
              </Button>
            </PermissionGuard>
          </div>
        </>
      )}
    </>
  );
};
